import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import BackendService from '../services/backendService';
import {
  PromptResourceMap,
  PromptSingleTestCaseResult,
} from '../services/backendService/types';
import { fromGenericMiltiTestCaseResultToSpecificTestCaseResult } from '../utils/aiGeneratorUtils';
import { DesignerUIStateActions, useDesignerUIContext } from './useDesignerUIState';

function useSingleSlideMultiResponsePromptTestCase(
  presentationId: string,
  slideId: string,
) {
  const testCaseResultCacheKey = useMemo(
    () => ['generateSingleSlideMulitResponsePromptTestCase', presentationId, slideId],
    [slideId, presentationId],
  );
  const client = useQueryClient();
  const dispatch = useDesignerUIContext(context => context.dispatch);

  const { mutate: generateExampleAnswers } = useMutation(
    ['singlePromptExampleAnswers', presentationId, slideId],
    BackendService.instance.generatorPlugin.generateExampleAnswers,
    {
      cacheTime: 1000 * 60 * 5,
    },
  );

  const { mutate: generate, isLoading, reset, error } = useMutation(
    testCaseResultCacheKey,
    BackendService.instance.generatorPlugin
      .generateSingleSlideMulitResponsePromptTestCase,
    {
      cacheTime: 1000 * 60 * 5,
      onSuccess: (data: PromptResourceMap) => {
        const updatedDataWithUniqueIds = fromGenericMiltiTestCaseResultToSpecificTestCaseResult(
          data,
          slideId,
        );

        // Sync with the designer state
        dispatch({
          type: DesignerUIStateActions.updatePromptDependentInputsBulk,
          payload: Object.values(updatedDataWithUniqueIds),
        });

        // Persist to react query so that it can be restored on page refresh
        client.setQueryData(
          ['persist', 'prompt-inputs', presentationId],
          (existing: PromptSingleTestCaseResult | undefined) => {
            if (!existing) {
              return updatedDataWithUniqueIds;
            }
            return {
              ...existing,
              ...updatedDataWithUniqueIds,
            };
          },
        );
      },
    },
  );

  const handleReset = () => {
    reset();
    client.removeQueries({ queryKey: testCaseResultCacheKey, exact: true });
  };

  return {
    generate,
    isLoading,
    reset: handleReset,
    error: JSON.stringify(error),
    generateExampleAnswers,
  };
}

export default useSingleSlideMultiResponsePromptTestCase;
